import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Save, RotateCcw, Loader2, Info } from "lucide-react";

import { formatApiError } from "@/lib/api";
import { useDefault } from "@/lib/useDefault";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const FREQ_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly (every 3 months)" },
  { value: "six_monthly", label: "Six-monthly (every 6 months)" },
  { value: "annual", label: "Annual (every 12 months)" },
  { value: "custom_days", label: "Custom (every N days)" },
];

// Server shape ⇄ form shape isolation lives in the page; transport is
// delegated to the shared useDefault hook.
const toForm = (d) => ({
  default_frequency: d.default_frequency,
  default_custom_days: d.default_custom_days == null ? "" : String(d.default_custom_days),
  anchor_offset_days: d.anchor_offset_days,
});

export default function SchedulingDefaults() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("settings.scheduling.write");
  const canReset = hasPermission("settings.scheduling.reset");

  const { value, loading, save, reset } = useDefault("scheduling");
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (value) {
      const shape = toForm(value);
      setForm(shape);
      setBaseline(shape);
    }
  }, [value]);

  const isCustom = form?.default_frequency === "custom_days";
  const dirty = baseline && form && JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    if (!canWrite || !form) return;
    const payload = { default_frequency: form.default_frequency };
    if (isCustom) {
      const n = parseInt(form.default_custom_days, 10);
      if (!Number.isFinite(n) || n < 1 || n > 3650) {
        toast.error("Custom days must be an integer in [1, 3650]");
        return;
      }
      payload.default_custom_days = n;
    } else {
      payload.default_custom_days = null;
    }
    const off = parseInt(form.anchor_offset_days, 10);
    if (!Number.isFinite(off) || off < 0 || off > 365) {
      toast.error("Anchor offset days must be an integer in [0, 365]");
      return;
    }
    payload.anchor_offset_days = off;
    setSaving(true);
    try {
      const data = await save(payload);
      const shape = toForm(data);
      setForm(shape);
      setBaseline(shape);
      toast.success("Scheduling defaults saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!canReset) return;
    if (!window.confirm("Reset scheduling defaults to the seeded values? Unsaved changes will be lost.")) return;
    try {
      const data = await reset();
      const shape = toForm(data);
      setForm(shape);
      setBaseline(shape);
      toast.success("Defaults reset");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading || !form) {
    return <div className="p-6" data-testid="settings-scheduling-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-6 pb-24" data-testid="settings-scheduling-page">
      <PageHeader
        testId="settings-scheduling-header"
        title="Scheduling Defaults"
        subtitle="Pre-populate the New Service Schedule form with sensible defaults. Users can still override any field per schedule."
        actions={
          <Button variant="outline" onClick={() => navigate("/settings")} data-testid="settings-scheduling-back">
            Back to Settings
          </Button>
        }
      />

      <Card className="rounded-md max-w-2xl">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" /> Defaults
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Frequency
            </Label>
            <Select
              value={form.default_frequency}
              onValueChange={(v) => setForm((f) => ({ ...f, default_frequency: v }))}
              disabled={!canWrite}
            >
              <SelectTrigger data-testid="settings-scheduling-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQ_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`settings-scheduling-frequency-option-${opt.value}`}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isCustom && (
            <div className="space-y-1.5" data-testid="settings-scheduling-custom-days-block">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Custom days <span className="text-destructive">*</span>
              </Label>
              <Input
                type="number"
                min={1}
                max={3650}
                value={form.default_custom_days}
                onChange={(e) => setForm((f) => ({ ...f, default_custom_days: e.target.value }))}
                disabled={!canWrite}
                data-testid="settings-scheduling-custom-days-input"
                placeholder="e.g. 45"
              />
              <div className="text-[10px] text-muted-foreground">Integer in [1, 3650]. Used when Frequency is Custom.</div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Anchor offset days
            </Label>
            <Input
              type="number"
              min={0}
              max={365}
              value={form.anchor_offset_days}
              onChange={(e) => setForm((f) => ({ ...f, anchor_offset_days: e.target.value }))}
              disabled={!canWrite}
              data-testid="settings-scheduling-anchor-offset-input"
            />
            <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
              <Info className="h-3 w-3 mt-0.5 shrink-0" />
              New schedules default their anchor date to today + N days. Range: [0, 365].
            </div>
          </div>
        </CardContent>
      </Card>

      {canWrite && (
        <div className="fixed bottom-0 left-56 right-0 bg-background border-t border-border p-3 flex justify-between items-center z-30" data-testid="settings-scheduling-actions-bar">
          <div className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "All saved"}
          </div>
          <div className="flex gap-2">
            {canReset && (
              <Button variant="outline" onClick={handleReset} data-testid="settings-scheduling-reset-btn">
                <RotateCcw className="h-4 w-4" /> Reset to defaults
              </Button>
            )}
            <Button variant="outline" onClick={() => setForm(baseline)} disabled={!dirty} data-testid="settings-scheduling-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dirty || saving} data-testid="settings-scheduling-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              <span className="ml-1">Save changes</span>
            </Button>
          </div>
        </div>
      )}

      {!canWrite && (
        <div className="text-xs text-muted-foreground italic" data-testid="settings-scheduling-readonly-notice">
          You have read-only access to scheduling defaults.
        </div>
      )}
    </div>
  );
}
